import { taskProgress } from '../models/task';
/**
 * Walking the project hierarchy.
 *
 * Nesting is unbounded in the model, and the tree view renders it, but every
 * other surface stopped at one level: opening a project listed its direct
 * children only, and its statistics counted only tasks whose `project_id` was
 * that exact project. A parent whose work all lives two levels down therefore
 * read "0 tasks · 0%" — not wrong-looking, just wrong.
 *
 * All of these are cycle-safe. A cycle can't be made through the UI (the
 * parent picker excludes descendants) but can arrive through an import, and a
 * hang is a worse failure than a truncated tree.
 */
/** Direct children, in the order they appear in `projects`. */
export function childProjects(projects, parentId) {
    return projects.filter((p) => p.parent_id === parentId);
}
/** Every project beneath this one, breadth-first, excluding itself. */
export function descendantProjects(projects, rootId) {
    const out = [];
    const seen = new Set([rootId]);
    const queue = [rootId];
    while (queue.length > 0) {
        const parentId = queue.shift();
        for (const child of projects) {
            if (child.parent_id !== parentId || seen.has(child.id))
                continue;
            seen.add(child.id);
            out.push(child);
            queue.push(child.id);
        }
    }
    return out;
}
/** The project itself plus everything beneath it, as ids. */
export function subtreeProjectIds(projects, rootId) {
    return [rootId, ...descendantProjects(projects, rootId).map((p) => p.id)];
}
/** How deep the tree under this project goes. 0 when it has no children. */
export function subtreeDepth(projects, rootId) {
    const depths = new Map([[rootId, 0]]);
    let max = 0;
    for (const project of descendantProjects(projects, rootId)) {
        const depth = (depths.get(project.parent_id ?? '') ?? 0) + 1;
        depths.set(project.id, depth);
        if (depth > max)
            max = depth;
    }
    return max;
}
/**
 * Ancestors then the project itself, root first — the trail a breadcrumb
 * renders. Returns an empty array for an unknown id.
 */
export function projectPath(projects, id) {
    const byId = new Map(projects.map((p) => [p.id, p]));
    const path = [];
    const seen = new Set();
    let current = byId.get(id);
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.unshift(current);
        current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    return path;
}
/** "Thesis › Experiments" — the ancestors only, without the project itself. */
export function projectPathLabel(projects, id, separator = ' › ') {
    return projectPath(projects, id).slice(0, -1).map((p) => p.name).join(separator);
}
/**
 * The numbers a project page should show. Habits and archived tasks are
 * excluded — a daily habit isn't project progress, and archived work isn't
 * outstanding — matching what the CSV export considers project work.
 */
export function projectRollup(projects, tasks, rootId) {
    const descendants = descendantProjects(projects, rootId);
    const ids = new Set([rootId, ...descendants.map((p) => p.id)]);
    const counted = tasks.filter((t) => t.project_id !== null && ids.has(t.project_id) && !t.is_habit && !t.is_archived);
    const own = counted.filter((t) => t.project_id === rootId);
    const completed = counted.filter((t) => t.status === 'completed');
    return {
        own: own.length,
        ownCompleted: own.filter((t) => t.status === 'completed').length,
        total: counted.length,
        completed: completed.length,
        open: counted.length - completed.length,
        progress: counted.length > 0
            ? counted.reduce((sum, t) => sum + taskProgress(t), 0) / counted.length
            : 0,
        remainingMinutes: counted
            .filter((t) => t.status !== 'completed')
            .reduce((sum, t) => sum + (t.estimated_minutes ?? 0), 0),
        subprojectCount: descendants.length,
    };
}
//# sourceMappingURL=project-tree.js.map